import '.a{display:grid;}.ssr-plugin'
import '.c{gap:1.5rem;}.ssr-plugin'
import { useMemo } from 'react'
import { useQuery } from '@based/sdk/react'
import Column from '@app/components/layout/Column'
import { Style, styled } from 'inlines'
import { Title } from '@app/components/core/typography/Title'
import { SearchTitle } from '@app/components/blocks/searchGrid/SearchTitle'
import { HousingFormCard } from '@app/components/blocks/card/HousingFormCard'
import { pages } from '@app/util/links'
import defaultHousingFormPicture from '@app/assets/defaultHousingFormPicture.png'
import useMediaQuery from '@app/util/useMediaQuery'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { compareSearchScore } from '@app/util/helpers'
import { useTranslations } from '@i18n/useLanguage'
import { HousingFormListModel } from 'functions/app/housing-form/list'
import { Empty } from '@app/components/blocks/empty/Empty'
import PeopleLookingForAHouseImage from '@app/assets/images/people_looking_for_house.png'

const HousingFormCardItem = ({
  housingForm,
  followerCount,
  to,
}: {
  housingForm: HousingFormListModel
  followerCount: number
  to: string
}) => {
  return (
    <HousingFormCard
      image={housingForm.images?.[0]?.src ?? defaultHousingFormPicture}
      title={housingForm.title}
      initiativeCount={housingForm.initiatives?.count ?? 0}
      followers={housingForm.followers ?? []}
      followerCount={followerCount}
      to={to}
    />
  )
}

export const DiscoverHousingForms = ({
  searchTerm,
  onResetSearch,
}: {
  searchTerm: string
  onResetSearch: () => void
}) => {
  const t = useTranslations().app.discoveryPage.tabs.housingForms
  const housingFormsQuery: { data?: HousingFormListModel[] } = useQuery(
    'list-housing-forms',
    {
      searchTerm: searchTerm,
    },
  )

  const followerCountQuery = useQuery('count-followers', {
    entityType: 'housingForm',
    entityIds: (housingFormsQuery.data ?? []).map((h) => h.id),
  })

  const { housingForms, followerCountById } = useMemo(() => {
    const data = housingFormsQuery.data || []

    const followerCountById = new Map<number, number>(
      data.map((item, index) => [
        item.id,
        followerCountQuery.data?.[index] ?? 0,
      ]),
    )

    const housingForms = data
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const aFollowerCount = followerCountQuery.data?.[a.index] ?? 0
        const bFollowerCount = followerCountQuery.data?.[b.index] ?? 0

        const scoreDiff = compareSearchScore(a.item, b.item)
        if (scoreDiff !== 0) {
          return scoreDiff
        }

        return bFollowerCount - aFollowerCount
      })
      .map((entry) => entry.item)

    return { housingForms, followerCountById }
  }, [housingFormsQuery.data, followerCountQuery.data])

  const isMobile = useMediaQuery()

  const gridStyle: Style = isMobile
    ? {
        gridTemplateColumns: 'minmax(0, 1fr)',
      }
    : {
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        '& > :nth-child(1)': {
          gridColumn: 'span 2',
        },
      }

  return (
    <Column alignItems="stretch" style={{ width: '100%' }} gap={0}>
      {searchTerm && (
        <Title.H3 style={{ alignSelf: 'start' }}>
          <SearchTitle searchTerm={searchTerm} onClick={onResetSearch} />
        </Title.H3>
      )}

      <Paragraph.Medium>{t.subtitle}</Paragraph.Medium>

      {housingForms.length === 0 ? (
        <Empty
          title={t.emptyTitle}
          imageSrc={PeopleLookingForAHouseImage}
          style={{ marginTop: '4rem' }}
        >
          {t.emptyDescription}
        </Empty>
      ) : (
        <styled.div className="a c"
          style={{
            ...gridStyle,
          }}
        >
          {housingForms.map((housingForm) => (
            <HousingFormCardItem
              key={housingForm.id}
              housingForm={housingForm}
              followerCount={followerCountById.get(housingForm.id) ?? 0}
              to={pages.housingForm(housingForm.slug)}
            />
          ))}
        </styled.div>
      )}
    </Column>
  )
}
